import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api, { downloadFile, getMediaBlobUrl } from '../../utils/api';
import { 
  DollarSign, Download, Trash2, Plus, Upload, Edit, Save, 
  Calendar, FileText, Eye, X, ChevronRight, Loader2, TrendingUp, BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useDropzone } from 'react-dropzone';
import BackButton from '../../components/BackButton';
import TwoStepUpload from '../../components/TwoStepUpload';
import useAuthStore from '../../store/authStore';

// Year range from 2020 to current year (2026)
const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];

// Professional color palette for years - financial credibility colors
const YEAR_COLORS = {
  2026: { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
  2025: { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  2024: { gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-500', light: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300' },
  2023: { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  2022: { gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-500', light: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300' },
  2021: { gradient: 'from-cyan-500 to-sky-600', bg: 'bg-cyan-500', light: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-300' },
  2020: { gradient: 'from-slate-500 to-gray-600', bg: 'bg-slate-500', light: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
};

const getYearColor = (year) => YEAR_COLORS[year] || YEAR_COLORS[2020];

const FinancialStatements = () => {
  const { user } = useAuthStore();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStatement, setEditingStatement] = useState(null);
  const [selectedYear, setSelectedYear] = useState('all');
  
  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewStatement, setPreviewStatement] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const [title, setTitle] = useState('');
  const [year, setYear] = useState(2026);
  const [quarter, setQuarter] = useState('');
  const [stagedFileUrl, setStagedFileUrl] = useState(null);
  const [stagedFileName, setStagedFileName] = useState(null);
  
  const [editTitle, setEditTitle] = useState('');
  const [editYear, setEditYear] = useState('');
  const [editQuarter, setEditQuarter] = useState('');
  const [editFile, setEditFile] = useState(null);

  const { getRootProps: getEditRootProps, getInputProps: getEditInputProps, isDragActive: isEditDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'], 'application/vnd.ms-excel': ['.xls'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
    maxFiles: 1,
    onDrop: (acceptedFiles) => setEditFile(acceptedFiles[0]),
  });

  useEffect(() => { loadStatements(); }, []);

  const loadStatements = async () => {
    try {
      const response = await api.get('/financial-statements');
      setStatements(response.data);
    } catch (error) {
      toast.error('Failed to load financial statements');
    } finally {
      setLoading(false);
    }
  };

  // Group statements by year
  const groupedStatements = useMemo(() => {
    const filtered = selectedYear === 'all' ? statements : statements.filter(s => s.year === parseInt(selectedYear));
    return filtered.reduce((acc, statement) => {
      const year = statement.year;
      if (!acc[year]) acc[year] = [];
      acc[year].push(statement);
      return acc;
    }, {});
  }, [statements, selectedYear]);

  // Stats
  const stats = useMemo(() => ({
    total: statements.length,
    years: [...new Set(statements.map(s => s.year))].length,
    thisYear: statements.filter(s => s.year === 2026).length,
  }), [statements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stagedFileUrl) { toast.error('Please upload and save a file first'); return; }
    setLoading(true);
    try {
      await api.post('/financial-statements', { title, file_url: stagedFileUrl, year: parseInt(year), quarter: quarter || null, uploaded_by: user.user_id });
      toast.success('Financial statement added successfully');
      setDialogOpen(false);
      resetForm();
      loadStatements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add financial statement');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSaved = (fileUrl, fileName) => {
    setStagedFileUrl(fileUrl);
    setStagedFileName(fileName);
  };

  const openEditDialog = (statement) => {
    setEditingStatement(statement);
    setEditTitle(statement.title);
    setEditYear(statement.year);
    setEditQuarter(statement.quarter || '');
    setEditFile(null);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingStatement) return;
    setLoading(true);
    try {
      let file_url = editingStatement.file_url;
      if (editFile) {
        const formData = new FormData();
        formData.append('file', editFile);
        const uploadResponse = await api.post('/files/upload', formData);
        file_url = uploadResponse.data.file_url;
      }
      await api.put(`/financial-statements/${editingStatement.id}`, { title: editTitle, year: parseInt(editYear), quarter: editQuarter || null, file_url });
      toast.success('Financial statement updated successfully');
      setEditDialogOpen(false);
      setEditingStatement(null);
      loadStatements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update financial statement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/financial-statements/${id}`);
      toast.success('Financial statement deleted');
      loadStatements();
    } catch (error) {
      toast.error('Failed to delete financial statement');
    }
  };

  const resetForm = () => { setTitle(''); setYear(2026); setQuarter(''); setStagedFileUrl(null); setStagedFileName(null); };

  const openPreview = async (statement) => {
    setPreviewStatement(statement);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const url = await getMediaBlobUrl(statement.file_url);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewStatement(null);
    if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const handleDownload = async (statement) => {
    try {
      await downloadFile(statement.file_url, statement.title);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-financial-page">
      <BackButton to="/admin" />
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-4 rounded-2xl shadow-lg">
            <DollarSign className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-serif font-bold text-slate-900">Financial Statements</h1>
            <p className="text-slate-400">Transparent financial records from 2020 onwards</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg">
              <Plus className="w-4 h-4 mr-2" />Add Statement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700">
            <DialogHeader><DialogTitle className="text-white">Upload Financial Statement</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Annual Financial Report 2024" required className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Financial Year</Label>
                  <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Quarter (Optional)</Label>
                  <Select value={quarter} onValueChange={setQuarter}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="Q1">Q1 (Jan-Mar)</SelectItem>
                      <SelectItem value="Q2">Q2 (Apr-Jun)</SelectItem>
                      <SelectItem value="Q3">Q3 (Jul-Sep)</SelectItem>
                      <SelectItem value="Q4">Q4 (Oct-Dec)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Upload Document (Two-Step: Upload → Save)</Label>
                <TwoStepUpload 
                  onSaveComplete={handleFileSaved}
                  onCancel={() => { setStagedFileUrl(null); setStagedFileName(null); }}
                  label="Upload Financial Document"
                  acceptedTypes={{
                    'application/pdf': ['.pdf'],
                    'application/vnd.ms-excel': ['.xls'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                  }}
                />
                {stagedFileUrl && (
                  <p className="text-sm text-emerald-400 mt-2">✓ File saved: {stagedFileName}</p>
                )}
              </div>
              <div className="flex space-x-2 pt-4">
                <Button type="submit" disabled={loading || !stagedFileUrl} className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600">{loading ? 'Adding...' : 'Add Statement'}</Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-400 text-sm font-medium">Total Statements</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <FileText className="w-8 h-8 text-emerald-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-400 text-sm font-medium">Years Covered</p>
              <p className="text-2xl font-bold text-slate-900">{stats.years}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-violet-400 text-sm font-medium">This Year (2026)</p>
              <p className="text-2xl font-bold text-slate-900">{stats.thisYear}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-violet-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-400 text-sm font-medium">Transparency</p>
              <p className="text-2xl font-bold text-slate-900">100%</p>
            </div>
            <BarChart3 className="w-8 h-8 text-amber-500/50" />
          </div>
        </div>
      </div>

      {/* Year Filter */}
      <div className="flex flex-wrap gap-2 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <span className="text-white font-medium text-sm self-center mr-2">Filter by Year:</span>
        <button onClick={() => setSelectedYear('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedYear === 'all' ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>All Years</button>
        {YEARS.map(y => {
          const colors = getYearColor(y);
          return (
            <button key={y} onClick={() => setSelectedYear(y.toString())} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedYear === y.toString() ? `bg-gradient-to-r ${colors.gradient} text-white shadow-lg` : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>{y}</button>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Edit Financial Statement</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Title</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Year</Label>
                <Select value={editYear.toString()} onValueChange={(v) => setEditYear(parseInt(v))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">{YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Quarter</Label>
                <Select value={editQuarter} onValueChange={setEditQuarter}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="Q1">Q1</SelectItem><SelectItem value="Q2">Q2</SelectItem><SelectItem value="Q3">Q3</SelectItem><SelectItem value="Q4">Q4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Replace Document (Optional)</Label>
              <div {...getEditRootProps()} className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer ${isEditDragActive ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-600 bg-slate-700/50'}`}>
                <input {...getEditInputProps()} />
                <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                {editFile ? <p className="text-sm text-emerald-400">New: {editFile.name}</p> : <p className="text-sm text-slate-400">Drop to replace</p>}
              </div>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600"><Save className="w-4 h-4 mr-2" />{loading ? 'Saving...' : 'Save Changes'}</Button>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      {previewOpen && previewStatement && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="relative max-w-4xl w-full max-h-[90vh] bg-slate-800 rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div>
                <h3 className="font-semibold text-white">{previewStatement.title}</h3>
                <p className="text-sm text-slate-400">{previewStatement.year} {previewStatement.quarter && `• ${previewStatement.quarter}`}</p>
              </div>
              <div className="flex items-center space-x-2">
                <Button onClick={() => handleDownload(previewStatement)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Download className="w-4 h-4 mr-2" />Download
                </Button>
                <button onClick={closePreview} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="h-[70vh] bg-slate-900">
              {previewLoading ? (
                <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
              ) : previewUrl ? (
                <iframe src={previewUrl} className="w-full h-full" title="Document Preview" />
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">Preview not available. Click Download to view.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Year-based Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      ) : Object.keys(groupedStatements).length > 0 ? (
        <div className="space-y-8">
          {Object.keys(groupedStatements).sort((a, b) => b - a).map((yearKey) => {
            const yearNum = parseInt(yearKey);
            const colors = getYearColor(yearNum);
            return (
              <div key={yearKey}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`bg-gradient-to-r ${colors.gradient} px-4 py-2 rounded-xl shadow-lg`}>
                    <span className="text-white font-bold text-lg">{yearKey}</span>
                  </div>
                  <div className="h-px flex-1 bg-gradient-to-r from-slate-700 to-transparent" />
                  <span className="text-slate-500 text-sm">{groupedStatements[yearKey].length} document(s)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupedStatements[yearKey].map((statement) => (
                    <div key={statement.id} className={`group bg-slate-800 rounded-2xl overflow-hidden border-2 border-slate-700 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/10`}>
                      <div className={`h-2 bg-gradient-to-r ${colors.gradient}`} />
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`bg-gradient-to-br ${colors.gradient} p-3 rounded-xl shadow-lg`}>
                            <DollarSign className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openEditDialog(statement)} className="p-2 text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded-lg" title="Edit"><Edit className="w-4 h-4" /></button>
                            <button onClick={() => handleDelete(statement.id, statement.title)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg" title="Delete"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                        <h3 className="font-semibold text-lg text-white mb-2 line-clamp-2 group-hover:text-emerald-400 transition-colors">{statement.title}</h3>
                        <div className="flex items-center space-x-2 text-sm text-slate-400 mb-4">
                          <Calendar className="w-4 h-4" />
                          <span>{statement.year}</span>
                          {statement.quarter && <span className={`${colors.bg} text-white px-2 py-0.5 rounded text-xs font-medium`}>{statement.quarter}</span>}
                        </div>
                        <div className="flex space-x-2">
                          <Button onClick={() => openPreview(statement)} variant="outline" size="sm" data-testid="preview-btn" className="flex-1 bg-slate-700 text-white border-slate-500 hover:bg-slate-600 hover:text-white hover:border-emerald-400">
                            <Eye className="w-4 h-4 mr-1" />Preview
                          </Button>
                          <Button onClick={() => handleDownload(statement)} size="sm" className={`flex-1 bg-gradient-to-r ${colors.gradient}`}>
                            <Download className="w-4 h-4 mr-1" />Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl text-center py-16">
          <DollarSign className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 text-lg">{selectedYear === 'all' ? 'No financial statements uploaded yet' : `No statements for ${selectedYear}`}</p>
          <p className="text-sm text-slate-500 mt-2">Click "Add Statement" to upload financial documents</p>
        </div>
      )}
    </div>
  );
};

export default FinancialStatements;
